import { Box, Typography } from '@mui/material';
import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { getEnterpriseIdFromToken } from '../component/context/EnterpriseConfigContext';
import { KitButton } from '../component/common/MobileKit';
import { KitSearch } from '../component/common/MobileKitExtras';
import { ProtoIcon } from '../component/common/ProtoIcon';
import { useNavSections } from '../component/layout/navConfig';
import { dashboardTabPath, useAllowedDashboardTabs } from './dashboards/dashboardTabs';
import { useIsMobileShell } from '../hooks/useIsMobileShell';
import { usePermissions } from '../hooks/usePermissions';

/**
 * Aba "Mais" do shell mobile — `prototipos-mobile/principal/mais.html`:
 * (a identidade — logo, usuário, empresa e plano — fica no header, MobileMoreHeader),
 * grupos de atalhos em grade 3 colunas (`.tilegrid`/`.tile`), "Sair da conta" e
 * versão. A seleção de tema fica no ícone de lua da barra superior (MobileLayout).
 *
 * Os atalhos vêm de `useNavSections()` (mesma fonte e mesmos gates de permissão
 * do menu lateral desktop). No desktop a rota redireciona para /home.
 */

// Ícone do protótipo para cada item do menu (`navConfig`), pelo id.
const PROTO_ICON_BY_ID = {
    'ai-assistant': 'sparkles',
    'dash-sale': 'chart',
    'dash-order': 'chart',
    'dash-vehicle': 'chart',
    'dash-quote': 'chart',
    'sale-new': 'cart',
    'sale-list': 'receipt',
    'order-new': 'orders',
    'order-list': 'orders',
    'order-upcoming': 'gauge',
    'quote-new': 'quote',
    'quote-list': 'quote',
    'sales-report': 'pie',
    'orderservice-report': 'pie',
    'product-report': 'pie',
    'stock-report': 'pie',
    'checklist-report': 'pie',
    'inventory-report': 'pie',
    'cash-flow-report': 'pie',
    'customer-report': 'pie',
    'vehicle-report': 'pie',
    purchases: 'cart',
    'accounts-payable': 'upright',
    'accounts-receivable': 'downleft',
    'os-billing': 'receipt',
    cashregisters: 'wallet',
    cardmachines: 'card',
    products: 'package',
    inventory: 'clipcheck',
    categories: 'grid',
    customers: 'users',
    vehicles: 'car',
    services: 'wrench',
    suppliers: 'truck',
    checklists: 'checks',
    users: 'user',
    profiles: 'shield',
    configuration: 'settings',
    auditlog: 'history',
    'admin-permissions': 'shield',
    'admin-subscriptions': 'badge',
    'admin-plans': 'award',
    'admin-configuration': 'settings',
    'admin-whatsapp': 'msg',
    plans: 'award',
};

function Tile({ icon, label, onClick }) {
    return (
        <Box
            component="button"
            type="button"
            onClick={onClick}
            sx={{
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '16px',
                p: '14px 8px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
                fontSize: 12,
                fontWeight: 600,
                lineHeight: 1.4,
                textAlign: 'center',
                boxShadow: '0 1px 2px rgba(15,23,42,.06)',
                color: 'text.primary',
                fontFamily: 'inherit',
                cursor: 'pointer',
                minWidth: 0,
            }}
        >
            <Box
                component="span"
                sx={{ width: 42, height: 42, borderRadius: '13px', backgroundColor: 'info.bg', color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
                <ProtoIcon name={icon} size={22} />
            </Box>
            {label}
        </Box>
    );
}

const MAX_RECENTS = 6;

function readJson(key, fallback) {
    try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : fallback;
    } catch (e) {
        return fallback;
    }
}

function writeJson(key, value) {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
        // sem storage (modo privado): a tela segue funcionando sem persistir
    }
}

function normalize(text) {
    return String(text || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

// Cabeçalho recolhível de um grupo de atalhos.
function GroupHeader({ label, count, open, onToggle }) {
    return (
        <Box
            component="button"
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                width: '100%',
                minHeight: 44,
                p: '0 4px',
                border: 0,
                background: 'none',
                fontFamily: 'inherit',
                cursor: 'pointer',
                color: 'text.primary',
                textAlign: 'left',
            }}
        >
            <Box component="span" sx={{ flex: 1, fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', color: 'text.disabled' }}>
                {label}
            </Box>
            <Box component="span" sx={{ fontSize: 11.5, fontWeight: 700, color: 'text.disabled' }}>{count}</Box>
            <Box component="span" sx={{ display: 'inline-flex', color: 'text.disabled', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>
                <ProtoIcon name="right" size={16} />
            </Box>
        </Box>
    );
}

function TileGrid({ items, onSelect }) {
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
            {items.map((item) => (
                <Tile key={item.id} icon={PROTO_ICON_BY_ID[item.id] || 'grid'} label={item.label} onClick={() => onSelect(item)} />
            ))}
        </Box>
    );
}

export default function MobileMore() {
    const navigate = useNavigate();
    const isMobileShell = useIsMobileShell();
    const sections = useNavSections();
    const dashboardTabs = useAllowedDashboardTabs();
    const { userData } = usePermissions();
    const storageKey = getEnterpriseIdFromToken() || 'anon';
    const [query, setQuery] = React.useState('');
    const [openGroups, setOpenGroups] = React.useState(() => readJson('more_open_groups:' + storageKey, []));
    const [recentIds, setRecentIds] = React.useState(() => readJson('more_recents:' + storageKey, []));
    if (!isMobileShell) return <Navigate to="/home" replace />;

    const isOwner = userData?.is_owner === true;

    const handleLogout = () => {
        // Mesma limpeza do AccountMenu (antes de apagar o token: a chave usa o enterprise_id dele).
        const key = getEnterpriseIdFromToken() || 'anon';
        sessionStorage.removeItem('sales_list_filters:' + key);
        sessionStorage.removeItem('service_orders_list_filters:' + key);
        sessionStorage.removeItem('service_quotes_list_filters:' + key);
        sessionStorage.removeItem('products_list_filters:' + key);
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user_permissions_cache');
        navigate('/login', { replace: true });
    };

    // "Início" já é uma aba da barra inferior; "Meu plano" (só owner) fecha o grupo Sistema.
    const groups = sections
        .map((section) => ({
            ...section,
            // No mobile não há barra de chips: o item único "Dashboards" vira um atalho por aba.
            items: section.items
                .filter((item) => item.id !== 'home')
                .flatMap((item) => (item.id === 'dashboards'
                    ? dashboardTabs.map((tab) => ({ id: `dash-${tab.id}`, label: tab.label, path: dashboardTabPath(tab.id) }))
                    : [item])),
        }))
        .map((section) => (section.id === 'sistema' && isOwner
            ? { ...section, items: [...section.items, { id: 'plans', label: 'Meu plano', path: '/plans' }] }
            : section))
        .filter((section) => section.items.length > 0);

    const allItems = groups.flatMap((section) => section.items);
    const recentItems = recentIds.map((id) => allItems.find((item) => item.id === id)).filter(Boolean);
    const needle = normalize(query.trim());
    const searching = needle.length > 0;
    const filteredGroups = searching
        ? groups
            .map((section) => ({
                ...section,
                items: section.items.filter((item) => normalize(item.label).includes(needle) || normalize(section.label).includes(needle)),
            }))
            .filter((section) => section.items.length > 0)
        : groups;

    const toggleGroup = (id) => {
        const next = openGroups.includes(id) ? openGroups.filter((g) => g !== id) : [...openGroups, id];
        setOpenGroups(next);
        writeJson('more_open_groups:' + storageKey, next);
    };

    const handleSelect = (item) => {
        const next = [item.id, ...recentIds.filter((id) => id !== item.id)].slice(0, MAX_RECENTS);
        setRecentIds(next);
        writeJson('more_recents:' + storageKey, next);
        navigate(item.path);
    };

    const buildVersion = process.env.REACT_APP_BUILD_VERSION;

    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }}>
            <KitSearch
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Buscar tela ou relatório…"
                inputProps={{ 'aria-label': 'Buscar no menu', enterKeyHint: 'search' }}
                sx={{ flex: 'none', width: '100%', mb: '6px' }}
            />

            {searching ? (
                filteredGroups.length === 0 ? (
                    <Typography sx={{ textAlign: 'center', fontSize: 13, color: 'text.disabled', py: '28px' }}>
                        Nada encontrado para “{query.trim()}”.
                    </Typography>
                ) : filteredGroups.map((section) => (
                    <Box key={section.id} sx={{ mt: '10px' }}>
                        <Box sx={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', color: 'text.disabled', p: '6px 4px 8px' }}>{section.label}</Box>
                        <TileGrid items={section.items} onSelect={handleSelect} />
                    </Box>
                ))
            ) : (
                <>
                    {recentItems.length > 0 && (
                        <Box sx={{ mt: '10px' }}>
                            <Box sx={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', color: 'text.disabled', p: '6px 4px 8px' }}>Recentes</Box>
                            <TileGrid items={recentItems} onSelect={handleSelect} />
                        </Box>
                    )}
                    <Box sx={{ mt: '10px' }}>
                        {groups.map((section, index) => {
                            const open = openGroups.includes(section.id);
                            return (
                                <Box key={section.id} sx={{ borderTop: index === 0 && recentItems.length === 0 ? 0 : '1px solid', borderColor: 'divider', pb: open ? '12px' : 0 }}>
                                    <GroupHeader label={section.label} count={section.items.length} open={open} onToggle={() => toggleGroup(section.id)} />
                                    {open && <TileGrid items={section.items} onSelect={handleSelect} />}
                                </Box>
                            );
                        })}
                    </Box>
                </>
            )}

            <Box sx={{ mt: '16px' }}>
                <KitButton variant="danger" block onClick={handleLogout} startIcon={<ProtoIcon name="logout" size={18} />}>
                    Sair da conta
                </KitButton>
            </Box>
            <Typography sx={{ textAlign: 'center', fontSize: 11.5, color: 'text.disabled', mt: '14px', lineHeight: 1.4 }}>
                FlowVendas{buildVersion ? ` v${buildVersion}` : ''}
            </Typography>
        </Box>
    );
}
